import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase/client';
import { Loader2, Eye, EyeOff, CheckCircle, AlertCircle, KeyRound } from 'lucide-react';
import logoEmblem from '@/assets/logos/vehix-logo-emblem.png';

export const AccountSetPasswordPage: React.FC = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCheckingSession, setIsCheckingSession] = useState(true);
  const [hasValidSession, setHasValidSession] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  // Check for valid session on mount
  useEffect(() => {
    const checkSession = async () => {
      setIsCheckingSession(true);

      try {
        const {
          data: { session },
          error: sessionError
        } = await supabase.auth.getSession();

        if (sessionError) {
          console.error('[AccountSetPasswordPage] Session check error:', sessionError);
          navigate('/invite/confirm', { replace: true });
          return;
        }

        if (!session) {
          console.log('[AccountSetPasswordPage] No session found, redirecting to OTP verification');
          navigate('/invite/confirm', { replace: true });
          return;
        }

        console.log('[AccountSetPasswordPage] Valid session found, showing password form');
        setHasValidSession(true);
      } catch (err) {
        console.error('[AccountSetPasswordPage] Error checking session:', err);
        navigate('/invite/confirm', { replace: true });
      } finally {
        setIsCheckingSession(false);
      }
    };

    checkSession();
  }, [navigate]);

  // Redirect after successful password set
  useEffect(() => {
    if (success) {
      setTimeout(() => {
        navigate('/', { replace: true });
      }, 2000);
    }
  }, [success, navigate]);

  const validatePassword = (pwd: string): string | null => {
    if (pwd.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    if (!/[A-Z]/.test(pwd)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/[a-z]/.test(pwd)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/[0-9]/.test(pwd)) {
      return 'Password must contain at least one number';
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) {
      return 'Password must contain at least one special character';
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password strength
    const validationError = validatePassword(password);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setIsSubmitting(true);

      // Update user password using Supabase Auth
      const { error: updateError } = await supabase.auth.updateUser({
        password: password
      });

      if (updateError) {
        throw updateError;
      }

      console.log('[AccountSetPasswordPage] Password updated successfully');
      setSuccess(true);

      // Update user metadata to track password was set
      await supabase.auth.updateUser({
        data: {
          password_set_at: new Date().toISOString()
        }
      });
    } catch (err) {
      console.error('[AccountSetPasswordPage] Password update error:', err);
      setError(err instanceof Error ? err.message : 'Failed to set password. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show loading state while checking session
  if (isCheckingSession) {
    return (
      <div className='min-h-screen bg-[#f3f0e8] flex items-center justify-center p-4'>
        <div className='w-full max-w-md bg-white rounded-lg shadow-lg p-8 text-center'>
          <div className='flex justify-center mb-4'>
            <img src={logoEmblem} alt='Vehix Logo' className='w-32 h-32' />
          </div>
          <Loader2 className='h-12 w-12 text-[#d4854a] mx-auto mb-4 animate-spin' />
          <h1 className='text-2xl font-bold text-gray-900 mb-2'>Preparing Your Account</h1>
          <p className='text-gray-600'>Please wait while we verify your session...</p>
        </div>
      </div>
    );
  }

  // Show success state
  if (success) {
    return (
      <div className='min-h-screen bg-[#f3f0e8] flex items-center justify-center p-4'>
        <div className='w-full max-w-md bg-white rounded-lg shadow-lg p-8 text-center'>
          <div className='flex justify-center mb-4'>
            <img src={logoEmblem} alt='Vehix Logo' className='w-32 h-32' />
          </div>
          <CheckCircle className='h-16 w-16 text-green-500 mx-auto mb-4' />
          <h1 className='text-2xl font-bold text-gray-900 mb-2'>Password Set Successfully!</h1>
          <p className='text-gray-600 mb-4'>
            Your account is ready. Redirecting you to the dashboard...
          </p>
        </div>
      </div>
    );
  }

  // Don't render form if no valid session (we'll redirect)
  if (!hasValidSession) {
    return null;
  }

  // Show password set form
  return (
    <div className='min-h-screen bg-[#f3f0e8] flex items-center justify-center p-4'>
      <div className='w-full max-w-md bg-white rounded-lg shadow-lg p-8'>
        <div className='text-center mb-6'>
          <div className='flex justify-center mb-4'>
            <img src={logoEmblem} alt='Vehix Logo' className='w-32 h-32' />
          </div>
          <div className='flex items-center justify-center mb-2'>
            <KeyRound className='h-6 w-6 text-[#d4854a] mr-2' />
            <h1 className='text-2xl font-bold text-gray-900'>Set Your Password</h1>
          </div>
          <p className='text-gray-600'>Create a secure password for your Vehix.ai account.</p>
        </div>

        {error && (
          <div
            className='mb-4 p-3 bg-red-50 border border-red-200 rounded-md
            flex items-center gap-2'
          >
            <AlertCircle className='h-5 w-5 text-red-600 flex-shrink-0' />
            <p className='text-sm text-red-600'>{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className='space-y-4'>
          <div>
            <label htmlFor='password' className='block text-sm font-medium text-gray-900 mb-2'>
              Password
            </label>
            <div className='relative'>
              <input
                id='password'
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder='Enter your password'
                className={[
                  'w-full px-3 py-2 pr-10 border border-gray-300 bg-white',
                  'text-gray-900 rounded-md focus:outline-none focus:ring-2',
                  'focus:ring-[#d4854a] focus:border-transparent placeholder:text-gray-400'
                ].join(' ')}
                required
                disabled={isSubmitting}
              />
              <button
                type='button'
                onClick={() => setShowPassword(!showPassword)}
                className='absolute right-3 top-2.5 text-gray-500 hover:text-gray-700'
                disabled={isSubmitting}
              >
                {showPassword ? <EyeOff className='h-5 w-5' /> : <Eye className='h-5 w-5' />}
              </button>
            </div>
            <p className='mt-1 text-xs text-gray-500'>
              Must be at least 8 characters with uppercase, lowercase, number, and special character
            </p>
          </div>

          <div>
            <label
              htmlFor='confirmPassword'
              className='block text-sm font-medium text-gray-900 mb-2'
            >
              Confirm Password
            </label>
            <div className='relative'>
              <input
                id='confirmPassword'
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder='Confirm your password'
                className={[
                  'w-full px-3 py-2 pr-10 border border-gray-300 bg-white',
                  'text-gray-900 rounded-md focus:outline-none focus:ring-2',
                  'focus:ring-[#d4854a] focus:border-transparent placeholder:text-gray-400'
                ].join(' ')}
                required
                disabled={isSubmitting}
              />
              <button
                type='button'
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className='absolute right-3 top-2.5 text-gray-500 hover:text-gray-700'
                disabled={isSubmitting}
              >
                {showConfirmPassword ? <EyeOff className='h-5 w-5' /> : <Eye className='h-5 w-5' />}
              </button>
            </div>
          </div>

          <button
            type='submit'
            disabled={isSubmitting || !password || !confirmPassword}
            className={[
              'w-full bg-[#d4854a] text-white py-2 px-4 rounded-md',
              'hover:bg-[#c17640] transition-colors font-medium',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              'flex items-center justify-center gap-2'
            ].join(' ')}
          >
            {isSubmitting ? (
              <>
                <Loader2 className='h-4 w-4 animate-spin' />
                Setting Password...
              </>
            ) : (
              'Set Password & Continue'
            )}
          </button>
        </form>

        <div className='mt-6 text-center'>
          <p className='text-sm text-gray-600'>
            Need help?{' '}
            <a
              href='mailto:contact@kebi.ai'
              className='text-[#d4854a] hover:text-[#c17640] font-semibold'
            >
              Contact Support
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};
